import { useState } from "react";
import { Box, Button, CircularProgress, List, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import WorkflowListItem from "./WorkflowListItem";
import NewWorkflowDialog from "./NewWorkflowDialog";
import type { NodeDef, WorkflowDefinition } from "./workflow.types";

interface WorkflowListRailProps {
  workflows: WorkflowDefinition[];
  loading: boolean;
  selectedId: string | null;
  triggerOptions: NodeDef[];
  width: number;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onUpdateDescription: (id: string, description: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onCreate: (payload: { name: string; trigger: { type: string } }) => Promise<unknown>;
  onGenerate: (request: string) => Promise<unknown>;
}

export default function WorkflowListRail({
  workflows,
  loading,
  selectedId,
  triggerOptions,
  width,
  onSelect,
  onRename,
  onUpdateDescription,
  onDuplicate,
  onDelete,
  onCreate,
  onGenerate,
}: WorkflowListRailProps) {
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const pendingDeleteWorkflow = workflows.find((w) => w._id === pendingDeleteId) || null;

  const handleCreate = async (payload: { name: string; trigger: { type: string } }) => {
    setIsCreating(true);
    try {
      await onCreate(payload);
      setNewDialogOpen(false);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        borderRight: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Button
          fullWidth
          size="small"
          variant="contained"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setNewDialogOpen(true)}
        >
          New workflow
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : workflows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            No workflows yet.
          </Typography>
        ) : (
          <List disablePadding>
            {workflows.map((workflow) => (
              <WorkflowListItem
                key={workflow._id}
                workflow={workflow}
                selected={workflow._id === selectedId}
                onSelect={() => onSelect(workflow._id)}
                onRename={(name) => onRename(workflow._id, name)}
                onUpdateDescription={(description) => onUpdateDescription(workflow._id, description)}
                onDuplicate={() => onDuplicate(workflow._id)}
                onDelete={() => setPendingDeleteId(workflow._id)}
              />
            ))}
          </List>
        )}
      </Box>

      <NewWorkflowDialog
        open={newDialogOpen}
        triggerOptions={triggerOptions}
        isSubmitting={isCreating}
        onCancel={() => setNewDialogOpen(false)}
        onCreate={handleCreate}
        onGenerate={async (request) => {
          setIsCreating(true);
          try {
            const generated = await onGenerate(request);
            if (generated) setNewDialogOpen(false);
          } finally {
            setIsCreating(false);
          }
        }}
      />

      <ConfirmDeleteModal
        open={!!pendingDeleteWorkflow}
        entityName={pendingDeleteWorkflow?.name || "this workflow"}
        description="Delete this workflow? This can't be undone."
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) onDelete(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </Box>
  );
}
